import { create, get, type CredentialCreationOptionsJSON, type CredentialRequestOptionsJSON } from '@github/webauthn-json';
import type { ApiClient } from '../apiClient';

export interface WebauthnRegisterPaths {
  setup: string;
  confirm: string;
}
export interface WebauthnLoginPaths {
  begin: string;
  finish: string;
}

export const DEFAULT_REGISTER_PATHS: WebauthnRegisterPaths = {
  setup: '/direct/usersvc/auth/mfa/webauthn/setup',
  confirm: '/direct/usersvc/auth/mfa/webauthn/confirm',
};

export const DEFAULT_LOGIN_PATHS: WebauthnLoginPaths = {
  begin: '/direct/usersvc/auth/webauthn/login/begin',
  finish: '/direct/usersvc/auth/webauthn/login/finish',
};

// Registers a discoverable passkey while authenticated. `navigator.credentials`
// lives here (browser-only), which is why this cannot live in shared-frontend-ui.
// The server's `options` JSON is the PublicKeyCredentialCreationOptionsJSON that
// @github/webauthn-json wraps into a real ceremony and re-serialises to base64url.
export async function registerPasskey(
  client: ApiClient,
  args: { password: string; nickname?: string },
  paths: WebauthnRegisterPaths = DEFAULT_REGISTER_PATHS,
): Promise<void> {
  const setup = await client.post<{ options: unknown; challenge_token: string }>(paths.setup, {
    password: args.password,
  });
  const credential = await create({ publicKey: setup.options } as CredentialCreationOptionsJSON);
  await client.post<void>(paths.confirm, {
    password: args.password,
    challenge_token: setup.challenge_token,
    credential,
    nickname: args.nickname,
  });
}

// Passwordless login. Returns the finish endpoint's `resp`; the real session JWT
// is delivered as the top-level envelope `token` and captured by the app's
// onResponse callback, so callers do not handle the token here. `finishExtra`
// lets the webclient thread its guest-cart token into the finish call.
export async function loginWithPasskey<T = unknown>(
  client: ApiClient,
  opts: { finishExtra?: Record<string, unknown>; paths?: WebauthnLoginPaths } = {},
): Promise<T> {
  const paths = opts.paths ?? DEFAULT_LOGIN_PATHS;
  const begin = await client.post<{ options: unknown; login_token: string }>(paths.begin, {});
  const credential = await get({ publicKey: begin.options } as CredentialRequestOptionsJSON);
  return client.post<T>(paths.finish, {
    login_token: begin.login_token,
    credential,
    ...(opts.finishExtra ?? {}),
  });
}

export interface WebauthnSignupPaths {
  begin: string;
  finish: string;
}

export const DEFAULT_SIGNUP_PATHS: WebauthnSignupPaths = {
  begin: '/direct/usersvc/auth/webauthn/signup/begin',
  finish: '/direct/usersvc/auth/webauthn/signup/finish',
};

// Passwordless account creation. begin() validates the email is free and returns
// creation options; create() mints the passkey; finish() creates the user +
// credential and returns the session token as the top-level envelope `token`
// (captured by the app's onResponse callback — callers don't handle it here).
// `finishExtra` threads the webclient guest-cart token.
export async function signupWithPasskey<T = unknown>(
  client: ApiClient,
  args: { email: string; name: string; nickname?: string; finishExtra?: Record<string, unknown> },
  paths: WebauthnSignupPaths = DEFAULT_SIGNUP_PATHS,
): Promise<T> {
  const begin = await client.post<{ options: unknown; challenge_token: string }>(paths.begin, {
    email: args.email,
    name: args.name,
  });
  const credential = await create({ publicKey: begin.options } as CredentialCreationOptionsJSON);
  return client.post<T>(paths.finish, {
    challenge_token: begin.challenge_token,
    credential,
    nickname: args.nickname,
    ...(args.finishExtra ?? {}),
  });
}
